// Auth0 SDK wrapper. Two responsibilities:
//   1. Mount Auth0Provider with env-driven config (parity with Angular AppModule.AuthModule.forRoot).
//   2. Wire Auth0's token getter into the shared Axios instance so feature code never
//      touches the Auth0 SDK directly.
//
// Mounted inside the router so onRedirectCallback can use useNavigate to honor deep-link
// returnTo after the hosted-login round trip.
import { Auth0Provider, AppState, useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { setAuthTokenGetter, setAuthLogoutHandler } from '@/api/axios';
import { env } from '@/api/env';
import { appConfig } from '@/api/appConfig';

function AuthBridge({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();

  useEffect(() => {
    setAuthTokenGetter(async () => {
      if (!isAuthenticated) return null;
      try {
        return await getAccessTokenSilently();
      } catch {
        return null;
      }
    });
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    setAuthLogoutHandler(() => {
      logout({ logoutParams: { returnTo: window.location.origin } });
    });
  }, [logout]);

  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  const savedLang =
    typeof localStorage !== 'undefined' ? localStorage.getItem('lang') : null;
  const uiLocale = savedLang ?? appConfig.defaultLanguage;

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo ?? '/', { replace: true });
  };

  return (
    <Auth0Provider
      domain={env.auth0.domain}
      clientId={env.auth0.clientId}
      authorizationParams={{
        audience: env.auth0.audience,
        scope: env.auth0.scope,
        connection: env.auth0.connection || undefined,
        ui_locales: uiLocale,
        redirect_uri: window.location.origin,
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}
    >
      <AuthBridge>{children}</AuthBridge>
    </Auth0Provider>
  );
}

// Router-friendly variant: use as a layout `element` in createBrowserRouter so the
// Auth0 context is available throughout the route tree.
export function AuthLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}
